import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { ROUTES } from '../../constants/routes';

/** Split one CSV line; supports quoted fields and doubled quotes. */
function splitCsvRow(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out.map((s) => {
    let t = s;
    if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) {
      t = t.slice(1, -1).replace(/""/g, '"');
    }
    return t.trim();
  });
}

/** CSV for bulk locations: property_code, site_name; optional site_address, site_id, public. */
function parseLocationImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iCode = headerParts.indexOf('property_code');
  const iName = headerParts.indexOf('site_name');
  const iAddr = headerParts.indexOf('site_address');
  const iSiteId = headerParts.indexOf('site_id');
  const iPublic = headerParts.indexOf('public');
  if (iCode === -1 || iName === -1) {
    return {
      error: `Missing required columns. Need property_code and site_name. Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({
      property_code: (parts[iCode] || '').trim(),
      site_name: (parts[iName] || '').trim(),
      site_address: iAddr >= 0 ? (parts[iAddr] || '').trim() : '',
      site_id: iSiteId >= 0 ? (parts[iSiteId] || '').trim() : '',
      public: iPublic >= 0 ? (parts[iPublic] || '').trim() : '',
    });
  }
  return { rows };
}

/** CSV for bulk residents: site_id, name; optional property_code, phone, email, is_active. */
function parseResidentImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iSiteId = headerParts.indexOf('site_id');
  const iName = headerParts.indexOf('name');
  const iCode = headerParts.indexOf('property_code');
  const iPhone = headerParts.indexOf('phone');
  const iEmail = headerParts.indexOf('email');
  const iActive = headerParts.indexOf('is_active');
  if (iSiteId === -1 || iName === -1) {
    return {
      error: `Missing required columns. Need site_id and name. Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({
      site_id: (parts[iSiteId] || '').trim(),
      name: (parts[iName] || '').trim(),
      property_code: iCode >= 0 ? (parts[iCode] || '').trim() : '',
      phone: iPhone >= 0 ? (parts[iPhone] || '').trim() : '',
      email: iEmail >= 0 ? (parts[iEmail] || '').trim() : '',
      is_active: iActive >= 0 ? (parts[iActive] || '').trim() : '',
    });
  }
  return { rows };
}

function downloadLocationImportTemplate() {
  const csv =
    'property_code,site_name,site_address,site_id,public\n' +
    'MY-COMMUNITY-01,Building A - Unit 1,123 Example Street,SITE-001,false\n' +
    'MY-COMMUNITY-01,Pool Area,,,true\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'locations-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** CSV for bulk properties (propertyimport.csv format). */
function parsePropertyImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iCode = headerParts.indexOf('property_id') >= 0 ? headerParts.indexOf('property_id') : headerParts.indexOf('property_code');
  const iName =
    headerParts.indexOf('property_name') >= 0 ? headerParts.indexOf('property_name') : headerParts.indexOf('name');
  const iCity = headerParts.indexOf('city');
  const iState = headerParts.indexOf('state');
  const iDiv = headerParts.indexOf('division_name');
  const iRegion = headerParts.indexOf('region');
  if (iCode === -1 || iName === -1) {
    return {
      error: `Missing required columns. Need property_id and property_name. Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({
      property_id: (parts[iCode] || '').trim(),
      property_name: (parts[iName] || '').trim(),
      city: iCity >= 0 ? (parts[iCity] || '').trim() : '',
      state: iState >= 0 ? (parts[iState] || '').trim() : '',
      division_name: iDiv >= 0 ? (parts[iDiv] || '').trim() : '',
      region: iRegion >= 0 ? (parts[iRegion] || '').trim() : '',
    });
  }
  return { rows };
}

function downloadPropertyImportTemplate() {
  const csv =
    'property_id,property_name,city,state,division_name,region\n' +
    '10393,Rolling Greens Village,Ocala,FL,Division 1,North\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'properties-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadResidentImportTemplate() {
  const csv =
    'site_id,name,property_code,phone,email,is_active\n' +
    'SITE-001,Jane Resident,MY-COMMUNITY-01,555-0100,jane@example.com,true\n' +
    'SITE-002,John Resident,,555-0200,,true\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'residents-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function catalogFnsForApp(catalog, appKey) {
  const a = catalog.find((c) => c.key === appKey);
  const f = a?.functions;
  if (!f) return [];
  if (Array.isArray(f)) return f;
  try {
    return JSON.parse(f);
  } catch {
    return [];
  }
}

function HomeAdminPanel() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Users');
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  const [rolesList, setRolesList] = useState([]);
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [rbacRoleId, setRbacRoleId] = useState('');
  const [rbacRows, setRbacRows] = useState([]);
  const [rbacBusy, setRbacBusy] = useState(false);
  const [newRoleKey, setNewRoleKey] = useState('');
  const [newRoleDisplay, setNewRoleDisplay] = useState('');
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLogType, setLogsLogType] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);

  const [versionDocsUrl, setVersionDocsUrl] = useState('');
  const [helpSettingsLoading, setHelpSettingsLoading] = useState(false);
  const [helpSettingsSaving, setHelpSettingsSaving] = useState(false);
  const [helpSettingsMsg, setHelpSettingsMsg] = useState('');

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [showImportLocationsModal, setShowImportLocationsModal] = useState(false);
  const [importParseError, setImportParseError] = useState('');
  const [importRows, setImportRows] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [showImportPropertiesModal, setShowImportPropertiesModal] = useState(false);
  const [propertyImportParseError, setPropertyImportParseError] = useState('');
  const [propertyImportRows, setPropertyImportRows] = useState([]);
  const [propertyImportResult, setPropertyImportResult] = useState(null);
  const [propertyImportBusy, setPropertyImportBusy] = useState(false);
  const [showImportResidentsModal, setShowImportResidentsModal] = useState(false);
  const [residentImportParseError, setResidentImportParseError] = useState('');
  const [residentImportRows, setResidentImportRows] = useState([]);
  const [residentImportResult, setResidentImportResult] = useState(null);
  const [residentImportBusy, setResidentImportBusy] = useState(false);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    property_id: '',
  });
  const [editingUser, setEditingUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
    role_id: '',
    property_id: '',
  });
  const [deletingUser, setDeletingUser] = useState({ id: null, name: '' });

  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    property_code: '',
    city: '',
    state: '',
    division_name: '',
    region: '',
  });
  const [editingProperty, setEditingProperty] = useState({
    id: null,
    name: '',
    address: '',
    property_code: '',
    city: '',
    state: '',
    division_name: '',
    region: '',
  });
  const [deletingProperty, setDeletingProperty] = useState({ id: null, name: '' });
  const [newLocation, setNewLocation] = useState({
    property_id: '',
    site_name: '',
    site_address: '',
    site_id: '',
    is_public: false,
  });
  const [editingLocation, setEditingLocation] = useState({
    id: null,
    property_id: '',
    site_name: '',
    site_address: '',
    site_id: '',
    is_public: false,
  });
  const [locationPropertyFilter, setLocationPropertyFilter] = useState('');
  const filteredLocations = locationPropertyFilter
    ? locations.filter((l) => String(l.property_id) === String(locationPropertyFilter))
    : locations;
  const [newResident, setNewResident] = useState({ location_id: '', name: '', phone: '', email: '', is_active: true });
  const [editingResident, setEditingResident] = useState({ id: null, location_id: '', name: '', phone: '', email: '', is_active: true });

  const [staffPropertyId, setStaffPropertyId] = useState('');
  const [staffForm, setStaffForm] = useState({});
  const [staffMsg, setStaffMsg] = useState('');

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    let ignore = false;
    const loadAll = async () => {
      try {
        const [usersRes, propertiesRes, locationsRes, residentsRes, ssoRes, rolesRes, catRes] = await Promise.all([
          axios.get('/api/users', { headers: authHeader() }),
          axios.get('/api/properties', { headers: authHeader() }),
          axios.get('/api/admin/locations', { headers: authHeader() }),
          axios.get('/api/admin/residents', { headers: authHeader() }),
          axios.get('/api/auth/sso-config'),
          axios.get('/api/admin/roles', { headers: authHeader() }),
          axios.get('/api/admin/roles/catalog', { headers: authHeader() }),
        ]);
        if (ignore) return;
        setUsers(usersRes.data);
        setProperties(propertiesRes.data);
        setLocations(locationsRes.data);
        setResidents(residentsRes.data);
        setSsoEnabled(Boolean(ssoRes.data?.microsoftEnabled));
        setRolesList(rolesRes.data);
        setRolesCatalog(catRes.data);
      } catch (_err) {
        if (!ignore) setError('Failed to load admin data');
      }
    };
    loadAll();
    return () => {
      ignore = true;
    };
  }, []);

  const roleKeyById = (rid) => rolesList.find((r) => Number(r.id) === Number(rid))?.key;

  const loadRoleAccess = async (roleId) => {
    if (!roleId) {
      setRbacRows([]);
      return;
    }
    try {
      const res = await axios.get(`/api/admin/roles/${roleId}/access`, { headers: authHeader() });
      const grants = res.data.map((row) => ({
        app_key: row.app_key,
        fn_key: row.fn_key != null && row.fn_key !== '' ? row.fn_key : '',
      }));
      setRbacRows(grants);
    } catch (_e) {
      setError('Failed to load role access');
    }
  };

  useEffect(() => {
    if (activeTab !== 'Roles' || !rbacRoleId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await loadRoleAccess(rbacRoleId);
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on tab/role change only
  }, [activeTab, rbacRoleId]);

  useEffect(() => {
    if (!staffPropertyId) {
      setStaffForm({});
      return;
    }
    axios
      .get(`/api/properties/${staffPropertyId}/staff`, { headers: authHeader() })
      .then((res) => {
        const row = res.data;
        setStaffForm({
          communityManagerName: row.community_manager_name || '',
          communityManagerEmail: row.community_manager_email || '',
          assistantManagerName: row.assistant_manager_name || '',
          assistantManagerEmail: row.assistant_manager_email || '',
          rvpName: row.rvp_name || '',
          rvpEmail: row.rvp_email || '',
        });
      })
      .catch(() => setStaffForm({}));
  }, [staffPropertyId]);

  const savePropertyStaff = async (e) => {
    e.preventDefault();
    if (!staffPropertyId) return;
    setError('');
    setStaffMsg('');
    try {
      await axios.put(`/api/properties/${staffPropertyId}/staff`, staffForm, { headers: authHeader() });
      setStaffMsg('Property staff saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save property staff');
    }
  };

  const saveRoleAccess = async () => {
    setRbacBusy(true);
    setError('');
    try {
      const grants = rbacRows
        .filter((row) => row.app_key)
        .map((row) => ({
          app_key: row.app_key,
          fn_key: row.fn_key === '' || row.fn_key == null ? null : row.fn_key,
        }));
      await axios.put(
        `/api/admin/roles/${rbacRoleId}/access`,
        { grants },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      await loadRoleAccess(rbacRoleId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save role access');
    } finally {
      setRbacBusy(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    setRbacBusy(true);
    setError('');
    try {
      await axios.post(
        '/api/admin/roles',
        { key: newRoleKey, display_name: newRoleDisplay },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setNewRoleKey('');
      setNewRoleDisplay('');
      const res = await axios.get('/api/admin/roles', { headers: authHeader() });
      setRolesList(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create role');
    } finally {
      setRbacBusy(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLogsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (logsLogType) params.set('log_type', logsLogType);
      params.set('limit', '150');
      const res = await axios.get(`/api/admin/activity-logs?${params.toString()}`, { headers: authHeader() });
      setActivityLogs(res.data);
    } catch (_err) {
      setError('Failed to load activity logs');
      setActivityLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'Logs') return undefined;
    fetchActivityLogs();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, logsLogType]);

  useEffect(() => {
    if (activeTab !== 'Help settings') return undefined;
    let cancelled = false;
    setHelpSettingsLoading(true);
    setHelpSettingsMsg('');
    axios
      .get('/api/admin/app-settings/help', { headers: authHeader() })
      .then((res) => {
        if (!cancelled) setVersionDocsUrl(res.data?.versionDocsUrl || '');
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load help settings');
      })
      .finally(() => {
        if (!cancelled) setHelpSettingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const saveHelpSettings = async (e) => {
    e.preventDefault();
    setHelpSettingsSaving(true);
    setHelpSettingsMsg('');
    setError('');
    try {
      await axios.put(
        '/api/admin/app-settings/version-docs-url',
        { url: versionDocsUrl.trim() },
        { headers: authHeader() }
      );
      setHelpSettingsMsg('Saved.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save help settings');
    } finally {
      setHelpSettingsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users', { headers: authHeader() });
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/api/properties', { headers: authHeader() });
      setProperties(response.data);
    } catch (err) {
      setError('Failed to load properties');
    }
  };

  const closeDeletePropertyModal = () => {
    setShowDeletePropertyModal(false);
    setDeletingProperty({ id: null, name: '' });
  };

  const closeAllModals = () => {
    setShowAddUserModal(false);
    setShowEditUserModal(false);
    setShowDeleteUserModal(false);
    setShowAddPropertyModal(false);
    setShowEditPropertyModal(false);
    closeDeletePropertyModal();
    setShowImportLocationsModal(false);
    setShowImportResidentsModal(false);
    setShowImportPropertiesModal(false);
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/admin/locations', { headers: authHeader() });
      setLocations(response.data);
    } catch (_err) {
      setError('Failed to load locations');
    }
  };

  const fetchResidents = async () => {
    try {
      const response = await axios.get('/api/admin/residents', { headers: authHeader() });
      setResidents(response.data);
    } catch (_err) {
      setError('Failed to load residents');
    }
  };

  const handleSsoToggle = async (enabled) => {
    setSsoSaving(true);
    setError('');
    try {
      await axios.put(
        '/api/auth/sso-config',
        { microsoftEnabled: enabled },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setSsoEnabled(enabled);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update SSO setting');
    } finally {
      setSsoSaving(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const rk = roleKeyById(newUser.role_id);
      if (rk === 'manager' && !newUser.property_id) {
        setError('Managers must have a property assigned');
        setLoading(false);
        return;
      }
      await axios.post('/api/users', {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role_id: Number(newUser.role_id),
        property_id: newUser.property_id || null,
      }, {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      const defaultRid = rolesList.find((x) => x.key === 'user')?.id || '';
      setNewUser({ name: '', email: '', password: '', role_id: defaultRid, property_id: '' });
      setShowAddUserModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        role_id: Number(editingUser.role_id),
        property_id: editingUser.property_id || null,
      };
      const rk = roleKeyById(editingUser.role_id);
      if (rk === 'manager' && !editingUser.property_id) {
        setError('Managers must have a property assigned');
        setLoading(false);
        return;
      }
      if (editingUser.password) updateData.password = editingUser.password;
      await axios.put(`/api/users/${editingUser.id}`, updateData, {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      setEditingUser({ id: null, name: '', email: '', password: '', role_id: '', property_id: '' });
      setShowEditUserModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.delete(`/api/users/${deletingUser.id}`, { headers: authHeader() });
      setDeletingUser({ id: null, name: '' });
      setShowDeleteUserModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const openImportPropertiesModal = () => {
    setPropertyImportParseError('');
    setPropertyImportRows([]);
    setPropertyImportResult(null);
    setShowImportPropertiesModal(true);
  };

  const handleImportPropertiesFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPropertyImportResult(null);
    setPropertyImportParseError('');
    setPropertyImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parsePropertyImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setPropertyImportParseError(parsed.error);
        return;
      }
      setPropertyImportRows(parsed.rows);
    };
    reader.onerror = () => setPropertyImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunPropertyImport = async () => {
    if (!propertyImportRows.length) return;
    setPropertyImportBusy(true);
    setError('');
    setPropertyImportResult(null);
    try {
      const res = await axios.post(
        '/api/properties/import',
        { rows: propertyImportRows },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setPropertyImportResult(res.data);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setPropertyImportBusy(false);
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/properties', newProperty, { headers: authHeader() });
      setNewProperty({ name: '', address: '', property_code: '', city: '', state: '', division_name: '', region: '' });
      setShowAddPropertyModal(false);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProperty = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.put(`/api/properties/${editingProperty.id}`, editingProperty, { headers: authHeader() });
      setEditingProperty({
        id: null,
        name: '',
        address: '',
        property_code: '',
        city: '',
        state: '',
        division_name: '',
        region: '',
      });
      setShowEditPropertyModal(false);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update property');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (!deletingProperty.id) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(`/api/properties/${deletingProperty.id}`, { headers: authHeader() });
      closeDeletePropertyModal();
      await fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete property');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/admin/locations', newLocation, {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      setNewLocation({ property_id: '', site_name: '', site_address: '', site_id: '', is_public: false });
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create location');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLocation = async (e) => {
    e.preventDefault();
    if (!editingLocation.id) return;
    setLoading(true);
    setError('');
    try {
      await axios.put(`/api/admin/locations/${editingLocation.id}`, editingLocation, {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      setEditingLocation({
        id: null,
        property_id: '',
        site_name: '',
        site_address: '',
        site_id: '',
        is_public: false,
      });
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Delete this location?')) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(`/api/admin/locations/${id}`, { headers: authHeader() });
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete location');
    } finally {
      setLoading(false);
    }
  };

  const openImportLocationsModal = () => {
    setImportParseError('');
    setImportRows([]);
    setImportResult(null);
    setShowImportLocationsModal(true);
  };

  const handleImportLocationsFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportResult(null);
    setImportParseError('');
    setImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseLocationImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setImportParseError(parsed.error);
        return;
      }
      setImportRows(parsed.rows);
    };
    reader.onerror = () => setImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunLocationImport = async () => {
    if (!importRows.length) return;
    setImportBusy(true);
    setError('');
    setImportResult(null);
    try {
      const res = await axios.post(
        '/api/admin/locations/import',
        { rows: importRows },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setImportResult(res.data);
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setImportBusy(false);
    }
  };

  const openImportResidentsModal = () => {
    setResidentImportParseError('');
    setResidentImportRows([]);
    setResidentImportResult(null);
    setShowImportResidentsModal(true);
  };

  const handleImportResidentsFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setResidentImportResult(null);
    setResidentImportParseError('');
    setResidentImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseResidentImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setResidentImportParseError(parsed.error);
        return;
      }
      setResidentImportRows(parsed.rows);
    };
    reader.onerror = () => setResidentImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunResidentImport = async () => {
    if (!residentImportRows.length) return;
    setResidentImportBusy(true);
    setError('');
    setResidentImportResult(null);
    try {
      const res = await axios.post(
        '/api/admin/residents/import',
        { rows: residentImportRows },
        { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
      );
      setResidentImportResult(res.data);
      fetchResidents();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setResidentImportBusy(false);
    }
  };

  const handleAddResident = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/admin/residents', newResident, {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      setNewResident({ location_id: '', name: '', phone: '', email: '', is_active: true });
      fetchResidents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create resident');
    } finally {
      setLoading(false);
    }
  };

  const handleEditResident = async (e) => {
    e.preventDefault();
    if (!editingResident.id) return;
    setLoading(true);
    setError('');
    try {
      await axios.put(`/api/admin/residents/${editingResident.id}`, editingResident, {
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
      });
      setEditingResident({ id: null, location_id: '', name: '', phone: '', email: '', is_active: true });
      fetchResidents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update resident');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResident = async (id) => {
    if (!window.confirm('Delete this resident?')) return;
    setLoading(true);
    setError('');
    try {
      await axios.delete(`/api/admin/residents/${id}`, { headers: authHeader() });
      fetchResidents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete resident');
    } finally {
      setLoading(false);
    }
  };

  const isAnyModalOpen =
    showAddUserModal ||
    showEditUserModal ||
    showDeleteUserModal ||
    showAddPropertyModal ||
    showEditPropertyModal ||
    showDeletePropertyModal ||
    showImportLocationsModal ||
    showImportResidentsModal ||
    showImportPropertiesModal;

  return (
    <div className="container-fluid mt-4 app-shell-page">
      <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <h1 className="h3 mb-1">Home Admin Panel</h1>
          <p className="text-muted small mb-0">Users, properties, property staff, locations, residents, roles, activity logs, and SSO.</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.HOME)}>
            Compass App
          </button>
          <span className="small text-muted">
            {user?.name} ({user?.role_key || user?.role})
          </span>
          <button onClick={handleLogout} className="btn btn-outline-danger">Logout</button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card app-card mb-4">
        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-1">Microsoft SSO Login</h5>
            <p className="text-muted mb-0 small">Enable or disable Microsoft SSO availability on the login page.</p>
          </div>
          <div className="form-check form-switch fs-5 mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="microsoftSsoToggle"
              checked={ssoEnabled}
              onChange={(e) => handleSsoToggle(e.target.checked)}
              disabled={ssoSaving}
            />
            <label className="form-check-label" htmlFor="microsoftSsoToggle">
              {ssoSaving ? 'Saving...' : ssoEnabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-3">
          <div className="list-group">
            {['Users', 'Properties', 'Property staff', 'Locations', 'Residents', 'Roles', 'Logs', 'Help settings'].map((item) => (
              <button
                key={item}
                className={`list-group-item list-group-item-action ${activeTab === item ? 'active' : ''}`}
                onClick={() => setActiveTab(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="col-md-9">
          <div className="card app-card">
            <div className="card-body">
              {activeTab === 'Users' && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>User Management</h3>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        const defaultRid = rolesList.find((x) => x.key === 'user')?.id || '';
                        setNewUser({ name: '', email: '', password: '', role_id: defaultRid, property_id: '' });
                        setShowAddUserModal(true);
                      }}
                    >
                      Add User
                    </button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Property</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.length > 0 ? users.map((u) => (
                          <tr key={u.id}>
                            <td>{u.id}</td>
                            <td>{u.name}</td>
                            <td>{u.email}</td>
                            <td>{u.role_key || u.role}</td>
                            <td>{u.property_name || 'No Property'}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => {
                                  setEditingUser({
                                    id: u.id,
                                    name: u.name,
                                    email: u.email,
                                    password: '',
                                    role_id: u.role_id,
                                    property_id: u.property_id || '',
                                  });
                                  setShowEditUserModal(true);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  setDeletingUser({ id: u.id, name: u.name });
                                  setShowDeleteUserModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">No users found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === 'Properties' && (
                <>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h3 className="mb-0">Property Management</h3>
                    <div className="d-flex gap-2">
                      <button type="button" className="btn btn-outline-primary" onClick={openImportPropertiesModal}>
                        Import properties (CSV)
                      </button>
                      <button type="button" className="btn btn-primary" onClick={() => setShowAddPropertyModal(true)}>
                        Add Property
                      </button>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Property code</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Division</th>
                          <th>Region</th>
                          <th>Address</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {properties.length > 0 ? properties.map((p) => (
                          <tr key={p.id}>
                            <td>{p.id}</td>
                            <td>{p.name}</td>
                            <td>{p.property_code || '—'}</td>
                            <td>{p.city || '—'}</td>
                            <td>{p.state || '—'}</td>
                            <td>{p.division_name || '—'}</td>
                            <td>{p.region || '—'}</td>
                            <td>{p.address || '—'}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => {
                                  setEditingProperty({
                                    id: p.id,
                                    name: p.name,
                                    address: p.address || '',
                                    property_code: p.property_code || '',
                                    city: p.city || '',
                                    state: p.state || '',
                                    division_name: p.division_name || '',
                                    region: p.region || '',
                                  });
                                  setShowEditPropertyModal(true);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                  setDeletingProperty({ id: p.id, name: p.name });
                                  setShowDeletePropertyModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="9" className="text-center text-muted">No properties found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === 'Property staff' && (
                <>
                  <h3 className="mb-2">Property staff</h3>
                  <p className="text-muted small mb-3">
                    Community manager, assistant manager, and RVP contacts per property. Used by Rules Violation and Hurricane.
                  </p>
                  {staffMsg && <div className="alert alert-success">{staffMsg}</div>}
                  <form className="card card-body" style={{ maxWidth: 560 }} onSubmit={savePropertyStaff}>
                    <label className="form-label">Property</label>
                    <select
                      className="form-select mb-3"
                      value={staffPropertyId}
                      onChange={(e) => setStaffPropertyId(e.target.value)}
                      required
                    >
                      <option value="">Select property…</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {['communityManager', 'assistantManager', 'rvp'].map((role) => (
                      <div key={role} className="mb-3">
                        <label className="form-label text-capitalize">
                          {role.replace(/([A-Z])/g, ' $1')}
                        </label>
                        <input
                          className="form-control form-control-sm mb-1"
                          placeholder="Name"
                          value={staffForm[`${role}Name`] || ''}
                          onChange={(e) => setStaffForm({ ...staffForm, [`${role}Name`]: e.target.value })}
                        />
                        <input
                          className="form-control form-control-sm"
                          placeholder="Email"
                          type="email"
                          value={staffForm[`${role}Email`] || ''}
                          onChange={(e) => setStaffForm({ ...staffForm, [`${role}Email`]: e.target.value })}
                        />
                      </div>
                    ))}
                    <button type="submit" className="btn btn-primary" disabled={!staffPropertyId}>
                      Save staff
                    </button>
                  </form>
                </>
              )}

              {activeTab === 'Locations' && (
                <>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h3 className="mb-0">Location Management</h3>
                    <button type="button" className="btn btn-outline-primary" onClick={openImportLocationsModal}>
                      Import locations (CSV)
                    </button>
                  </div>
                  <form className="row g-2 mb-3" onSubmit={editingLocation.id ? handleEditLocation : handleAddLocation}>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={(editingLocation.id ? editingLocation.property_id : newLocation.property_id) || ''}
                        onChange={(e) => (
                          editingLocation.id
                            ? setEditingLocation({ ...editingLocation, property_id: e.target.value })
                            : setNewLocation({ ...newLocation, property_id: e.target.value })
                        )}
                        required
                      >
                        <option value="">Property...</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.property_code ? ` (${p.property_code})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <input
                        className="form-control"
                        placeholder="Site ID"
                        value={editingLocation.id ? editingLocation.site_id : newLocation.site_id}
                        onChange={(e) => (
                          editingLocation.id
                            ? setEditingLocation({ ...editingLocation, site_id: e.target.value })
                            : setNewLocation({ ...newLocation, site_id: e.target.value })
                        )}
                      />
                    </div>
                    <div className="col-md-2">
                      <input
                        className="form-control"
                        placeholder="Site Name"
                        value={editingLocation.id ? editingLocation.site_name : newLocation.site_name}
                        onChange={(e) => (
                          editingLocation.id
                            ? setEditingLocation({ ...editingLocation, site_name: e.target.value })
                            : setNewLocation({ ...newLocation, site_name: e.target.value })
                        )}
                        required
                      />
                    </div>
                    <div className="col-md-2">
                      <input
                        className="form-control"
                        placeholder="Site Address"
                        value={editingLocation.id ? editingLocation.site_address : newLocation.site_address}
                        onChange={(e) => (
                          editingLocation.id
                            ? setEditingLocation({ ...editingLocation, site_address: e.target.value })
                            : setNewLocation({ ...newLocation, site_address: e.target.value })
                        )}
                      />
                    </div>
                    <div className="col-md-2 d-flex align-items-center">
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="location-is-public"
                          checked={
                            editingLocation.id ? !!editingLocation.is_public : !!newLocation.is_public
                          }
                          onChange={(e) =>
                            editingLocation.id
                              ? setEditingLocation({ ...editingLocation, is_public: e.target.checked })
                              : setNewLocation({ ...newLocation, is_public: e.target.checked })
                          }
                        />
                        <label className="form-check-label" htmlFor="location-is-public">
                          Public location
                        </label>
                      </div>
                    </div>
                    <div className="col-md-1 d-flex gap-2">
                      <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                        {editingLocation.id ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </form>
                  <div className="row g-2 mb-3">
                    <div className="col-md-4">
                      <label className="form-label small mb-1">Filter by property</label>
                      <select
                        className="form-select form-select-sm"
                        value={locationPropertyFilter}
                        onChange={(e) => setLocationPropertyFilter(e.target.value)}
                      >
                        <option value="">All properties</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.property_code ? ` (${p.property_code})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4 d-flex align-items-end">
                      <span className="small text-muted">
                        {filteredLocations.length} location{filteredLocations.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Property</th>
                          <th>Site ID</th>
                          <th>Site Name</th>
                          <th>Site Address</th>
                          <th>Public</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLocations.length > 0 ? filteredLocations.map((l) => (
                          <tr key={l.id}>
                            <td>{l.id}</td>
                            <td>{l.property_name || l.property_id}</td>
                            <td>{l.site_id || '—'}</td>
                            <td>{l.site_name || l.name}</td>
                            <td>{l.site_address || '-'}</td>
                            <td>{l.is_public ? 'Yes' : 'No'}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => setEditingLocation({
                                  id: l.id,
                                  property_id: l.property_id || '',
                                  site_name: l.site_name || l.name || '',
                                  site_address: l.site_address || '',
                                  site_id: l.site_id || '',
                                  is_public: !!l.is_public,
                                })}
                              >
                                Edit
                              </button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteLocation(l.id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="7" className="text-center text-muted">
                              {locations.length === 0
                                ? 'No locations found'
                                : 'No locations for this property'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === 'Residents' && (
                <>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h3 className="mb-0">Resident Management</h3>
                    <button type="button" className="btn btn-outline-primary" onClick={openImportResidentsModal}>
                      Import residents (CSV)
                    </button>
                  </div>
                  <p className="text-muted small mb-3">
                    CSV import links each row to a location by <strong>site_id</strong> (set on Locations first).
                  </p>
                  <form className="row g-2 mb-3" onSubmit={editingResident.id ? handleEditResident : handleAddResident}>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={(editingResident.id ? editingResident.location_id : newResident.location_id) || ''}
                        onChange={(e) => (
                          editingResident.id
                            ? setEditingResident({ ...editingResident, location_id: e.target.value })
                            : setNewResident({ ...newResident, location_id: e.target.value })
                        )}
                        required
                      >
                        <option value="">Location...</option>
                        {locations.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.site_name || l.name}
                            {l.site_id ? ` (${l.site_id})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input
                        className="form-control"
                        placeholder="Resident Name"
                        value={editingResident.id ? editingResident.name : newResident.name}
                        onChange={(e) => (
                          editingResident.id
                            ? setEditingResident({ ...editingResident, name: e.target.value })
                            : setNewResident({ ...newResident, name: e.target.value })
                        )}
                        required
                      />
                    </div>
                    <div className="col-md-2">
                      <input
                        className="form-control"
                        placeholder="Phone"
                        value={editingResident.id ? editingResident.phone : newResident.phone}
                        onChange={(e) => (
                          editingResident.id
                            ? setEditingResident({ ...editingResident, phone: e.target.value })
                            : setNewResident({ ...newResident, phone: e.target.value })
                        )}
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        className="form-control"
                        placeholder="Email"
                        value={editingResident.id ? editingResident.email : newResident.email}
                        onChange={(e) => (
                          editingResident.id
                            ? setEditingResident({ ...editingResident, email: e.target.value })
                            : setNewResident({ ...newResident, email: e.target.value })
                        )}
                      />
                    </div>
                    <div className="col-md-1 d-flex gap-2">
                      <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                        {editingResident.id ? 'Update' : 'Add'}
                      </button>
                    </div>
                  </form>
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Location</th>
                          <th>Site ID</th>
                          <th>Phone</th>
                          <th>Email</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {residents.length > 0 ? residents.map((r) => (
                          <tr key={r.id}>
                            <td>{r.id}</td>
                            <td>{r.name}</td>
                            <td>{r.site_name || r.location_id}</td>
                            <td>{r.location_site_id || '—'}</td>
                            <td>{r.phone || '-'}</td>
                            <td>{r.email || '-'}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => setEditingResident({ id: r.id, location_id: r.location_id || '', name: r.name || '', phone: r.phone || '', email: r.email || '', is_active: r.is_active !== false })}
                              >
                                Edit
                              </button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteResident(r.id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="7" className="text-center text-muted">No residents found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {activeTab === 'Roles' && (
                <>
                  <h3 className="mb-2">Roles and access</h3>
                  <p className="text-muted small mb-3">
                    Choose a role, then add rows: pick a sub-application and optionally a specific function.
                    Leave the function as &quot;(entire sub-app)&quot; to grant all functions for that app.
                  </p>
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label className="form-label">Role to edit</label>
                      <select
                        className="form-select"
                        value={rbacRoleId}
                        onChange={(e) => setRbacRoleId(e.target.value)}
                      >
                        <option value="">Select…</option>
                        {rolesList.map((r) => (
                          <option key={r.id} value={String(r.id)}>
                            {r.display_name} ({r.key})
                            {r.is_system ? ' — system' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {rbacRoleId && (
                    <>
                      <div className="table-responsive mb-2">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Sub-application</th>
                              <th>Function</th>
                              <th style={{ width: '90px' }} />
                            </tr>
                          </thead>
                          <tbody>
                            {rbacRows.map((row, idx) => (
                              <tr key={`${idx}-${row.app_key}-${row.fn_key}`}>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={row.app_key}
                                    onChange={(e) => {
                                      const next = [...rbacRows];
                                      next[idx] = { app_key: e.target.value, fn_key: '' };
                                      setRbacRows(next);
                                    }}
                                  >
                                    <option value="">—</option>
                                    {rolesCatalog.map((a) => (
                                      <option key={a.key} value={a.key}>{a.display_name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="form-select form-select-sm"
                                    value={row.fn_key}
                                    onChange={(e) => {
                                      const next = [...rbacRows];
                                      next[idx] = { ...next[idx], fn_key: e.target.value };
                                      setRbacRows(next);
                                    }}
                                    disabled={!row.app_key}
                                  >
                                    <option value="">(entire sub-app)</option>
                                    {catalogFnsForApp(rolesCatalog, row.app_key).map((fn) => (
                                      <option key={fn.key} value={fn.key}>{fn.display_name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setRbacRows(rbacRows.filter((_, i) => i !== idx))}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="d-flex flex-wrap gap-2 mb-4">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setRbacRows([...rbacRows, { app_key: '', fn_key: '' }])}
                        >
                          Add grant row
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={rbacBusy}
                          onClick={() => saveRoleAccess()}
                        >
                          {rbacBusy ? 'Saving…' : 'Save access'}
                        </button>
                      </div>
                      <hr />
                      <h5 className="mb-2">Create custom role</h5>
                      <form className="row g-2 align-items-end" onSubmit={handleCreateRole}>
                        <div className="col-md-3">
                          <label className="form-label small">Key (snake_case)</label>
                          <input
                            className="form-control form-control-sm"
                            value={newRoleKey}
                            onChange={(e) => setNewRoleKey(e.target.value)}
                            placeholder="e.g. maintenance_lead"
                            required
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small">Display name</label>
                          <input
                            className="form-control form-control-sm"
                            value={newRoleDisplay}
                            onChange={(e) => setNewRoleDisplay(e.target.value)}
                            placeholder="Maintenance lead"
                            required
                          />
                        </div>
                        <div className="col-md-2">
                          <button type="submit" className="btn btn-sm btn-success" disabled={rbacBusy}>
                            Create
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </>
              )}

              {activeTab === 'Logs' && (
                <>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <h3 className="mb-0">Activity logs</h3>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <label className="form-label small mb-0">Log type</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto', minWidth: '160px' }}
                        value={logsLogType}
                        onChange={(e) => setLogsLogType(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="user">User (logins)</option>
                        <option value="error">Errors</option>
                      </select>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => fetchActivityLogs()} disabled={logsLoading}>
                        Refresh
                      </button>
                    </div>
                  </div>
                  <p className="text-muted small mb-3">
                    <strong>User</strong> rows are successful logins (user, IP, time). <strong>Error</strong> rows include the page URL, user when known, and the error message (client or server).
                  </p>
                  {logsLoading ? (
                    <p className="text-muted">Loading…</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped">
                        <thead className="table-light">
                          <tr>
                            <th>Time</th>
                            <th>Type</th>
                            <th>User</th>
                            <th>IP</th>
                            <th>Page URL</th>
                            <th>Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityLogs.length === 0 ? (
                            <tr><td colSpan="6" className="text-center text-muted">No entries</td></tr>
                          ) : (
                            activityLogs.map((row) => (
                              <tr key={row.id}>
                                <td className="text-nowrap small">{new Date(row.created_at).toLocaleString()}</td>
                                <td><span className="badge bg-secondary">{row.log_type}</span></td>
                                <td className="small">
                                  {row.user_id ? (
                                    <>
                                      {row.user_name || '—'}
                                      <br />
                                      <span className="text-muted">{row.user_email || ''}</span>
                                    </>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td className="small text-break font-monospace">{row.ip_address || '—'}</td>
                                <td className="small text-break" style={{ maxWidth: '260px' }} title={row.page_url || ''}>{row.page_url || '—'}</td>
                                <td className="small text-break" style={{ maxWidth: '320px' }} title={row.message || ''}>{row.message || '—'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {activeTab === 'Help settings' && (
                <>
                  <h3 className="mb-3">Help settings</h3>
                  <p className="text-muted small">
                    Shown on the Help page from the Compass home screen (version documentation link).
                  </p>
                  {helpSettingsLoading ? (
                    <p className="text-muted">Loading…</p>
                  ) : (
                    <form onSubmit={saveHelpSettings} className="mt-3" style={{ maxWidth: '640px' }}>
                      <div className="mb-3">
                        <label className="form-label" htmlFor="versionDocsUrl">
                          Version documentation URL
                        </label>
                        <input
                          id="versionDocsUrl"
                          type="url"
                          className="form-control"
                          placeholder="https://..."
                          value={versionDocsUrl}
                          onChange={(e) => setVersionDocsUrl(e.target.value)}
                        />
                        <div className="form-text">
                          SharePoint or any HTTPS link. Leave empty to hide the documentation link on Help.
                        </div>
                      </div>
                      {helpSettingsMsg && (
                        <div className="alert alert-success py-2 small">{helpSettingsMsg}</div>
                      )}
                      <button type="submit" className="btn btn-primary" disabled={helpSettingsSaving}>
                        {helpSettingsSaving ? 'Saving…' : 'Save'}
                      </button>
                    </form>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      </div>

      {showAddUserModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add User</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddUserModal(false)} />
              </div>
              <form onSubmit={handleAddUser}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
                    <div className="form-text">User must change this password on first login.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={newUser.role_id}
                      onChange={(e) => setNewUser({ ...newUser, role_id: e.target.value })}
                      required
                    >
                      <option value="">Select role…</option>
                      {rolesList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.display_name} ({r.key})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Property
                      {roleKeyById(newUser.role_id) === 'manager' ? <span className="text-danger"> *</span> : ' (optional)'}
                    </label>
                    <select className="form-select" value={newUser.property_id} onChange={(e) => setNewUser({ ...newUser, property_id: e.target.value })}>
                      <option value="">No Property Assigned</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.property_code ? ` (${p.property_code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddUserModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create User'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditUserModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditUserModal(false)} />
              </div>
              <form onSubmit={handleEditUser}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input type="email" className="form-control" value={editingUser.email} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input type="password" className="form-control" placeholder="Leave blank to keep current password" value={editingUser.password} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })} />
                    <div className="form-text">If you set a new password, the user must change it on next login.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={editingUser.role_id}
                      onChange={(e) => setEditingUser({ ...editingUser, role_id: e.target.value })}
                      required
                    >
                      {rolesList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.display_name} ({r.key})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Property
                      {roleKeyById(editingUser.role_id) === 'manager' ? <span className="text-danger"> *</span> : ' (optional)'}
                    </label>
                    <select className="form-select" value={editingUser.property_id || ''} onChange={(e) => setEditingUser({ ...editingUser, property_id: e.target.value })}>
                      <option value="">No Property Assigned</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.property_code ? ` (${p.property_code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditUserModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Updating...' : 'Update User'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDeleteUserModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete User</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteUserModal(false)} />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete <strong>{deletingUser.name}</strong>?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteUserModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteUser} disabled={loading}>{loading ? 'Deleting...' : 'Delete User'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportPropertiesModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import properties</h5>
                <button type="button" className="btn-close" onClick={() => setShowImportPropertiesModal(false)} />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV like <strong>propertyimport.csv</strong>: required columns{' '}
                  <strong>property_id</strong> (stored as property code) and <strong>property_name</strong> (name).
                  Optional: <strong>city</strong>, <strong>state</strong>, <strong>division_name</strong>,{' '}
                  <strong>region</strong>. Existing rows with the same property code are updated.
                </p>
                <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={downloadPropertyImportTemplate}>
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input type="file" accept=".csv,text/csv" className="form-control" onChange={handleImportPropertiesFile} />
                </div>
                {propertyImportParseError && (
                  <div className="alert alert-danger py-2 small">{propertyImportParseError}</div>
                )}
                {propertyImportRows.length > 0 && !propertyImportParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{propertyImportRows.length}</strong> row
                    {propertyImportRows.length === 1 ? '' : 's'}.
                  </div>
                )}
                {propertyImportResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{propertyImportResult.created}</strong> created,{' '}
                      <strong>{propertyImportResult.updated}</strong> updated,{' '}
                      <strong>{propertyImportResult.skipped}</strong> skipped.
                    </p>
                    {propertyImportResult.errors?.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: 220 }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>property_id</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {propertyImportResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.property_code ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImportPropertiesModal(false)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={propertyImportBusy || !propertyImportRows.length || !!propertyImportParseError}
                  onClick={handleRunPropertyImport}
                >
                  {propertyImportBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddPropertyModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Property</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddPropertyModal(false)} />
              </div>
              <form onSubmit={handleAddProperty}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={newProperty.name} onChange={(e) => setNewProperty({ ...newProperty, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Property code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProperty.property_code}
                      onChange={(e) => setNewProperty({ ...newProperty, property_code: e.target.value })}
                      placeholder="e.g. 10393 (reporting / location import)"
                      autoComplete="off"
                    />
                  </div>
                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">City</label>
                      <input className="form-control" value={newProperty.city} onChange={(e) => setNewProperty({ ...newProperty, city: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">State</label>
                      <input className="form-control" value={newProperty.state} onChange={(e) => setNewProperty({ ...newProperty, state: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Division name</label>
                      <input className="form-control" value={newProperty.division_name} onChange={(e) => setNewProperty({ ...newProperty, division_name: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Region</label>
                      <input className="form-control" value={newProperty.region} onChange={(e) => setNewProperty({ ...newProperty, region: e.target.value })} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea className="form-control" rows="2" value={newProperty.address} onChange={(e) => setNewProperty({ ...newProperty, address: e.target.value })} />
                    <div className="form-text">Optional; import sets this from city and state when blank.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAddPropertyModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create Property'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showEditPropertyModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Property</h5>
                <button type="button" className="btn-close" onClick={() => setShowEditPropertyModal(false)} />
              </div>
              <form onSubmit={handleEditProperty}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={editingProperty.name} onChange={(e) => setEditingProperty({ ...editingProperty, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Property code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingProperty.property_code}
                      onChange={(e) => setEditingProperty({ ...editingProperty, property_code: e.target.value })}
                      autoComplete="off"
                    />
                  </div>
                  <div className="row g-2 mb-3">
                    <div className="col-md-6">
                      <label className="form-label">City</label>
                      <input className="form-control" value={editingProperty.city} onChange={(e) => setEditingProperty({ ...editingProperty, city: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">State</label>
                      <input className="form-control" value={editingProperty.state} onChange={(e) => setEditingProperty({ ...editingProperty, state: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Division name</label>
                      <input className="form-control" value={editingProperty.division_name} onChange={(e) => setEditingProperty({ ...editingProperty, division_name: e.target.value })} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Region</label>
                      <input className="form-control" value={editingProperty.region} onChange={(e) => setEditingProperty({ ...editingProperty, region: e.target.value })} />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea className="form-control" rows="2" value={editingProperty.address} onChange={(e) => setEditingProperty({ ...editingProperty, address: e.target.value })} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditPropertyModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Updating...' : 'Update Property'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showImportResidentsModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import residents</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportResidentsModal(false)}
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV with required columns <strong>site_id</strong> and <strong>name</strong>. Optional:{' '}
                  <strong>property_code</strong> (when the same site_id could exist on multiple properties),{' '}
                  <strong>phone</strong>, <strong>email</strong>, <strong>is_active</strong> (true/false).
                  Each <code>site_id</code> must match a location&apos;s Site ID from Location Management.
                </p>
                <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={downloadResidentImportTemplate}>
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={handleImportResidentsFile}
                  />
                </div>
                {residentImportParseError && (
                  <div className="alert alert-danger py-2 small">{residentImportParseError}</div>
                )}
                {residentImportRows.length > 0 && !residentImportParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{residentImportRows.length}</strong> row
                    {residentImportRows.length === 1 ? '' : 's'}.
                  </div>
                )}
                {residentImportResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{residentImportResult.created}</strong> created,{' '}
                      <strong>{residentImportResult.skipped}</strong> skipped.
                    </p>
                    {residentImportResult.errors?.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: 220 }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>site_id</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {residentImportResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.site_id ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {residentImportResult.errors.length > 50 && (
                          <p className="small text-muted mt-1 mb-0">Showing first 50 errors.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImportResidentsModal(false)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={residentImportBusy || !residentImportRows.length || !!residentImportParseError}
                  onClick={handleRunResidentImport}
                >
                  {residentImportBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImportLocationsModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import locations</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportLocationsModal(false)}
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV with columns <strong>property_code</strong>, <strong>site_name</strong>, and
                  optional <strong>site_address</strong>, <strong>site_id</strong>, <strong>public</strong>{' '}
                  (<code>true</code>/<code>yes</code>/<code>1</code> for a public location; omit or <code>false</code> otherwise).
                  Each <code>property_code</code> must match an <strong>active</strong> property&apos;s code (case-insensitive).
                </p>
                <button type="button" className="btn btn-sm btn-outline-secondary mb-3" onClick={downloadLocationImportTemplate}>
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={handleImportLocationsFile}
                  />
                </div>
                {importParseError && <div className="alert alert-danger py-2 small">{importParseError}</div>}
                {importRows.length > 0 && !importParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{importRows.length}</strong> row{importRows.length === 1 ? '' : 's'}.
                  </div>
                )}
                {importResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{importResult.created}</strong> created, <strong>{importResult.skipped}</strong> skipped.
                    </p>
                    {importResult.errors?.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: 220 }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>property_code</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.property_code ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {importResult.errors.length > 50 && (
                          <p className="small text-muted mt-1 mb-0">Showing first 50 errors.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowImportLocationsModal(false)}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={importBusy || !importRows.length || !!importParseError}
                  onClick={handleRunLocationImport}
                >
                  {importBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeletePropertyModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Property</h5>
                <button type="button" className="btn-close" onClick={closeDeletePropertyModal} />
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete <strong>{deletingProperty.name}</strong>?
                </p>
                <p className="small text-muted mb-0">
                  This also removes locations, residents, service requests, inspections, hurricane/rules
                  reports, and other data tied to this property. This cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeDeletePropertyModal}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteProperty} disabled={loading}>{loading ? 'Deleting...' : 'Delete Property'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAnyModalOpen && (
        <div className="modal-backdrop fade show" onClick={closeAllModals} />
      )}
    </div>
  );
}

export default HomeAdminPanel;
